import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

import Carousel from '../../../components/carousel';
import GenericCategoryGraph, { chartTypeEnum } from '../../../components/Graphs/GenericCategoryGraph';
import { PageHeader } from '../../../components/PageHeader';
import LoadingRenderer from '../../../components/LoadingRenderer';
import OrgSelect, { NO_ORG } from '../../../components/OrgSelect/OrgSelect';
import Strings from '../../../Strings';
import { DEFAULT_TIMEZONE } from '../../../constants';
import actions from './redux/actions';
import MessageDetailsTab from './MessageDetailsTab';

import './MessagesOverview.scss';
import './SuperuserHealthCenter.scss';

const MESSAGE_RANGE_PRESETS = [
  { value: 'last_1h', label: 'Hourly' },
  { value: 'last_24h', label: '24 hours' },
  { value: 'last_7d', label: '7 days' },
  { value: 'last_28d', label: '28 days' },
];

const STATUS_COLORS = ['#205375', '#2c7da0', '#61a5c2', '#4d908e', '#c94f4f', '#a0aec0', '#6b7280'];
const FAILED_REASON_COLORS = ['#c94f4f', '#d96c6c', '#e08e79', '#f4a261', '#b56576', '#6d597a', '#577590'];
const SMS_TYPE_COLORS = ['#4d908e', '#2c7da0', '#90be6d', '#f9844a', '#577590', '#43aa8b', '#277da1'];
const ORG_COLORS = ['#205375', '#3d5a80', '#98c1d9', '#ee6c4d', '#7b9e87', '#8d99ae', '#457b9d'];

const MESSAGE_PAGE_TABS = {
  Overview: 'Overview',
  Details: 'Details',
};

function getPresetDurationMs(preset) {
  switch (preset) {
    case 'last_1h':
      return 60 * 60 * 1000;
    case 'last_24h':
      return 24 * 60 * 60 * 1000;
    case 'last_7d':
      return 7 * 24 * 60 * 60 * 1000;
    case 'last_28d':
    case 'last_30d':
      return 28 * 24 * 60 * 60 * 1000;
    default:
      return 24 * 60 * 60 * 1000;
  }
}

function formatRangeLabel(startAt, endAt) {
  const sameDay = startAt.toDateString() === endAt.toDateString();
  const dateFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
  const dateTimeFmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (sameDay) {
    return `${dateTimeFmt.format(startAt)} - ${new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(endAt)}`;
  }
  return `${dateFmt.format(startAt)} - ${dateTimeFmt.format(endAt)}`;
}

function formatShortRangeLabel(startAt, endAt) {
  const durationMs = endAt.getTime() - startAt.getTime();
  const sameDay = startAt.toDateString() === endAt.toDateString();
  const sameYear = startAt.getFullYear() === endAt.getFullYear();
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (durationMs <= 24 * 60 * 60 * 1000) {
    if (sameDay) {
      return `${dateFormatter.format(startAt)}, ${timeFormatter.format(startAt)} - ${timeFormatter.format(endAt)}`;
    }
    return `${dateFormatter.format(startAt)}, ${timeFormatter.format(startAt)} - ${dateFormatter.format(endAt)}, ${timeFormatter.format(endAt)}`;
  }

  if (sameDay) {
    return dateFormatter.format(startAt);
  }

  return `${dateFormatter.format(startAt)} - ${dateFormatter.format(endAt)}`;
}

function hexToRgba(hex, alpha) {
  const clean = hex.replace('#', '');
  const normalized = clean.length === 3
    ? clean.split('').map(char => char + char).join('')
    : clean;
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function fadePalette(colors = [], alpha = 0.35) {
  return colors.map(color => hexToRgba(color, alpha));
}

function buildSinglePeriodComparison({
  currentValue,
  previousValue,
  currentStartAt,
  currentEndAt,
  previousStartAt,
  previousEndAt,
}) {
  return [{
    label: 'Selected period',
    current: currentValue || 0,
    previous: previousValue || 0,
    current_start_at: currentStartAt,
    current_end_at: currentEndAt,
    previous_start_at: previousStartAt,
    previous_end_at: previousEndAt,
  }];
}

function SummaryCard({ title, value, subtitle, accent }) {
  return (
    <div className="health-messages-card">
      <div className="health-messages-card__label">{title}</div>
      <div className="health-messages-card__value">{value}</div>
      <div className={`health-messages-card__meta ${accent ? `is-${accent}` : ''}`}>{subtitle}</div>
    </div>
  );
}

SummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  subtitle: PropTypes.node.isRequired,
  accent: PropTypes.string,
};

function BreakdownSection({ title, children }) {
  return (
    <div className="health-messages-section">
      <div className="health-messages-section__title">{title}</div>
      {children}
    </div>
  );
}

BreakdownSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function PeriodPanelTitle({ label, range }) {
  return (
    <div className="health-messages-panel-title-block">
      <div className="health-messages-panel-title-block__label">{label}</div>
      <div className="health-messages-panel-title-block__range">{range}</div>
    </div>
  );
}

PeriodPanelTitle.propTypes = {
  label: PropTypes.string.isRequired,
  range: PropTypes.string.isRequired,
};

function formatCompactNumber(value) {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value || 0);
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="health-messages-tooltip">
      <div className="health-messages-tooltip__title">{label}</div>
      {payload.map(entry => (
        <div key={entry.name} className="health-messages-tooltip__row">
          <span className="health-messages-tooltip__label">{entry.name}</span>
          <span className="health-messages-tooltip__value">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};

function MessageVolumeChart({
  title,
  data,
  currentLabel,
  previousLabel,
  color,
  previousColor = '#8fa3b8',
  compactTicks,
  onCurrentClick,
  onPreviousClick,
}) {
  return (
    <div className="health-messages-panel">
      <div className="health-messages-panel__header">
        <div className="health-messages-panel__title">{title}</div>
      </div>
      <div className="health-messages-panel__body is-chart">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} barGap={8} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e6edf2" />
            <XAxis
              dataKey="label"
              tick={{ fill: '#54606c', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              interval={0}
              minTickGap={compactTicks ? 0 : 8}
              tickFormatter={value => {
                if (!compactTicks) return value;
                return value;
              }}
            />
            <YAxis tick={{ fill: '#54606c', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip />} />
            <Legend />
            <Bar
              dataKey="current"
              name={currentLabel}
              fill={color}
              radius={[6, 6, 0, 0]}
              onClick={barData => onCurrentClick?.(barData?.payload)}
              cursor={onCurrentClick ? 'pointer' : 'default'}
            />
            <Bar
              dataKey="previous"
              name={previousLabel}
              fill={previousColor}
              radius={[6, 6, 0, 0]}
              onClick={barData => onPreviousClick?.(barData?.payload)}
              cursor={onPreviousClick ? 'pointer' : 'default'}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

MessageVolumeChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  currentLabel: PropTypes.string.isRequired,
  previousLabel: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  previousColor: PropTypes.string,
  compactTicks: PropTypes.bool,
  onCurrentClick: PropTypes.func,
  onPreviousClick: PropTypes.func,
};

function BreakdownList({ title, items, renderValue, barValue, barColor }) {
  const maxBarValue = React.useMemo(() => {
    if (!barValue || !items?.length) return 0;
    return Math.max(...items.map(item => barValue(item) || 0), 0);
  }, [barValue, items]);

  return (
    <div className="health-messages-panel">
      <div className="health-messages-panel__header">
        <div className="health-messages-panel__title">{title}</div>
      </div>
      <div className="health-messages-panel__body">
        {items?.length ? (
          <div className="health-messages-list">
            {items.map(item => (
              <div className="health-messages-list__row" key={item.key}>
                <div className="health-messages-list__row-top">
                  <div className="health-messages-list__label">{item.label}</div>
                  <div className="health-messages-list__value">{renderValue(item)}</div>
                </div>
                {barValue ? (
                  <div className="health-messages-list__bar-track">
                    <div
                      className="health-messages-list__bar-fill"
                      style={{
                        width: `${maxBarValue ? (barValue(item) / maxBarValue) * 100 : 0}%`,
                        backgroundColor: barColor || '#205375',
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="health-messages-empty">{Strings.superuserHealthNoData || 'No data yet.'}</div>
        )}
      </div>
    </div>
  );
}

BreakdownList.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  renderValue: PropTypes.func.isRequired,
  barValue: PropTypes.func,
  barColor: PropTypes.string,
};

function StatusBreakdownChart({ title, data, colorPalette = STATUS_COLORS, onBarClick }) {
  return (
    <div className="health-messages-panel">
      <div className="health-messages-panel__header">
        <div className="health-messages-panel__title">{title}</div>
      </div>
      <div className="health-messages-panel__body is-chart">
        <GenericCategoryGraph
          graphData={data}
          graphType={chartTypeEnum.horizontalBar}
          dataSeries={[{ name: Strings.messages?.messages || 'Messages', color: colorPalette[0], selector: item => item.count }]}
          categorySelector={item => item.label}
          tooltipTitle="Messages"
          yAxisWidth={120}
          onCategoryClicked={label => onBarClick?.(data.find(item => item.label === label))}
        />
      </div>
    </div>
  );
}

StatusBreakdownChart.propTypes = {
  title: PropTypes.node.isRequired,
  data: PropTypes.array.isRequired,
  colorPalette: PropTypes.arrayOf(PropTypes.string),
  onBarClick: PropTypes.func,
};

function HorizontalBreakdownChart({ title, data, colorPalette, yAxisWidth = 220, onBarClick }) {
  return (
    <div className="health-messages-panel">
      <div className="health-messages-panel__header">
        <div className="health-messages-panel__title">{title}</div>
      </div>
      <div className="health-messages-panel__body is-chart">
        <GenericCategoryGraph
          graphData={data}
          graphType={chartTypeEnum.horizontalBar}
          dataSeries={[{ name: Strings.messages?.messages || 'Messages', color: colorPalette[0], selector: item => item.count }]}
          categorySelector={item => item.label}
          tooltipTitle="Messages"
          yAxisWidth={yAxisWidth}
          onCategoryClicked={label => onBarClick?.(data.find(item => item.label === label))}
        />
      </div>
    </div>
  );
}

HorizontalBreakdownChart.propTypes = {
  title: PropTypes.node.isRequired,
  data: PropTypes.array.isRequired,
  colorPalette: PropTypes.arrayOf(PropTypes.string).isRequired,
  yAxisWidth: PropTypes.number,
  onBarClick: PropTypes.func,
};

function formatPercentWithCount(label, count, total) {
  const ratio = total ? (count || 0) / total : 0;
  return `${(ratio * 100).toFixed(1)}% ${label} (${formatCompactNumber(count)})`;
}

function formatStatusLabel(status) {
  switch (status) {
    case 'skipped_test':
      return Strings.superuserHealthTestSms || 'Test SMS';
    default:
      return status;
  }
}

function normalizeTypeItems(items = []) {
  return (items || []).map(item => ({
    key: item.context,
    label: item.context,
    count: item.count,
    context: item.context,
  }));
}

function normalizeStatusItems(items = []) {
  return (items || []).map(item => ({
    key: item.status,
    label: formatStatusLabel(item.status),
    count: item.count,
    status: item.status,
  }));
}

function normalizeFailedReasonItems(items = []) {
  return (items || []).map(item => ({
    key: item.reason,
    label: item.reason,
    count: item.count,
    reason: item.reason,
  }));
}

function normalizeOrgItems(items = []) {
  return (items || []).map(item => ({
    key: `${item.org_id}-${item.org_name}`,
    label: item.org_name,
    count: item.total,
    total: item.total,
    failed: item.failed,
    skipped_test: item.skipped_test,
    org_id: item.org_id,
    org_name: item.org_name,
  }));
}

function renderSummarySubtitle(data) {
  return (
    <>
      <div>{formatPercentWithCount('failed', data?.failed, data?.total)}</div>
      <div>{formatPercentWithCount('test number', data?.skipped_test, data?.total)}</div>
    </>
  );
}

function buildMessageDrilldownBuckets(series, endAtMs, bucket) {
  if (!series?.length) {
    return [];
  }

  if (bucket === 'hour') {
    const endHourMs = new Date(endAtMs).setMinutes(0, 0, 0);
    const startHourMs = endHourMs - (23 * 60 * 60 * 1000);
    return series.map((item, index) => {
      const bucketStartMs = startHourMs + (index * 60 * 60 * 1000);
      const bucketEndMs = bucketStartMs + (60 * 60 * 1000);
      return {
        ...item,
        current_start_at: new Date(bucketStartMs).toISOString(),
        current_end_at: new Date(bucketEndMs).toISOString(),
        previous_start_at: new Date(bucketStartMs - (24 * 60 * 60 * 1000)).toISOString(),
        previous_end_at: new Date(bucketEndMs - (24 * 60 * 60 * 1000)).toISOString(),
      };
    });
  }

  if (bucket === 'week') {
    const currentEndDate = new Date(endAtMs);
    currentEndDate.setHours(0, 0, 0, 0);
    const startWeekMs = currentEndDate.getTime() - (27 * 24 * 60 * 60 * 1000);
    const previousStartWeekMs = startWeekMs - (28 * 24 * 60 * 60 * 1000);
    return series.map((item, index) => {
      const bucketStartMs = startWeekMs + (index * 7 * 24 * 60 * 60 * 1000);
      const bucketEndMs = bucketStartMs + (7 * 24 * 60 * 60 * 1000);
      const previousBucketStartMs = previousStartWeekMs + (index * 7 * 24 * 60 * 60 * 1000);
      return {
        ...item,
        current_start_at: new Date(bucketStartMs).toISOString(),
        current_end_at: new Date(bucketEndMs).toISOString(),
        previous_start_at: new Date(previousBucketStartMs).toISOString(),
        previous_end_at: new Date(previousBucketStartMs + (7 * 24 * 60 * 60 * 1000)).toISOString(),
      };
    });
  }

  const currentEndDate = new Date(endAtMs);
  currentEndDate.setHours(0, 0, 0, 0);
  const startDayMs = currentEndDate.getTime() - (6 * 24 * 60 * 60 * 1000);
  const previousStartDayMs = startDayMs - (7 * 24 * 60 * 60 * 1000);
  return series.map((item, index) => {
    const bucketStartMs = startDayMs + (index * 24 * 60 * 60 * 1000);
    const bucketEndMs = bucketStartMs + (24 * 60 * 60 * 1000);
    const previousBucketStartMs = previousStartDayMs + (index * 24 * 60 * 60 * 1000);
    return {
      ...item,
      current_start_at: new Date(bucketStartMs).toISOString(),
      current_end_at: new Date(bucketEndMs).toISOString(),
      previous_start_at: new Date(previousBucketStartMs).toISOString(),
      previous_end_at: new Date(previousBucketStartMs + (24 * 60 * 60 * 1000)).toISOString(),
    };
  });
}

function MessagesOverview({ getMessagesOverview, orgIds }) {
  const [activeTab, setActiveTab] = React.useState(MESSAGE_PAGE_TABS.Overview);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [overview, setOverview] = React.useState(null);
  const [drilldownRequest, setDrilldownRequest] = React.useState(null);
  const [rangePreset, setRangePreset] = React.useState('last_24h');
  const [rangeEndAt, setRangeEndAt] = React.useState(null);
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const getMessagesOverviewRef = React.useRef(getMessagesOverview);

  const timezone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE, []);
  const rangeDurationMs = React.useMemo(() => getPresetDurationMs(rangePreset), [rangePreset]);
  const effectiveEndAtMs = React.useMemo(() => (rangeEndAt === null ? Date.now() : rangeEndAt), [rangeEndAt]);
  const effectiveStartAtMs = effectiveEndAtMs - rangeDurationMs;
  const isAtLatestRange = rangeEndAt === null;
  const rangeLabel = React.useMemo(
    () => formatRangeLabel(new Date(effectiveStartAtMs), new Date(effectiveEndAtMs)),
    [effectiveEndAtMs, effectiveStartAtMs],
  );

  React.useEffect(() => {
    getMessagesOverviewRef.current = getMessagesOverview;
  }, [getMessagesOverview]);

  const requestParams = React.useMemo(() => {
    const params =
      rangeEndAt === null
        ? { timezone, range_mode: 'presets', preset: rangePreset }
        : {
            timezone,
            range_mode: 'custom',
            start_at: new Date(effectiveStartAtMs).toISOString(),
            end_at: new Date(effectiveEndAtMs).toISOString(),
          };
    if (orgIds && orgIds.length > 0 && !orgIds.includes(NO_ORG)) {
      params.org_uuid = orgIds;
    }
    return params;
  }, [effectiveEndAtMs, effectiveStartAtMs, orgIds, rangeEndAt, rangePreset, timezone]);
  const requestKey = React.useMemo(
    () => JSON.stringify({ ...requestParams, refreshNonce }),
    [requestParams, refreshNonce],
  );

  React.useEffect(() => {
    setIsLoading(true);
    setError('');

    getMessagesOverviewRef.current(requestParams)
      .then(resp => {
        setOverview(resp?.response || null);
        setIsLoading(false);
      })
      .catch(() => {
        setError(Strings.superuserHealthLoadError || 'Unable to load health monitor data.');
        setIsLoading(false);
      });
  }, [requestKey, requestParams]);

  const moveRangeLeft = React.useCallback(() => {
    setRangeEndAt(prev => (prev ?? Date.now()) - rangeDurationMs);
  }, [rangeDurationMs]);

  const moveRangeRight = React.useCallback(() => {
    setRangeEndAt(prev => {
      if (prev === null) return null;
      const candidate = prev + rangeDurationMs;
      if (candidate >= Date.now()) return null;
      return candidate;
    });
  }, [rangeDurationMs]);

  const onPresetChange = React.useCallback(event => {
    setRangePreset(event.target.value);
    setRangeEndAt(null);
  }, []);

  const onRefresh = React.useCallback(() => {
    setRefreshNonce(prev => prev + 1);
  }, []);

  const summary = overview?.summary || {};
  const breakdowns = overview?.breakdowns || {};
  const timeseriesBucket = overview?.timeseries?.current_vs_previous?.bucket || 'hour';
  const appliedFilters = overview?.filters || {};
  const timeseries = React.useMemo(() => {
    if (rangePreset === 'last_1h') {
      return buildSinglePeriodComparison({
        currentValue: summary.current?.sms,
        previousValue: summary.previous?.sms,
        currentStartAt: appliedFilters.start_at,
        currentEndAt: appliedFilters.end_at,
        previousStartAt: appliedFilters.previous_start_at,
        previousEndAt: appliedFilters.previous_end_at,
      });
    }
    return buildMessageDrilldownBuckets(overview?.timeseries?.current_vs_previous?.channels?.sms || [], effectiveEndAtMs, timeseriesBucket);
  }, [
    appliedFilters.end_at,
    appliedFilters.previous_end_at,
    appliedFilters.previous_start_at,
    appliedFilters.start_at,
    effectiveEndAtMs,
    overview?.timeseries?.current_vs_previous?.channels?.sms,
    rangePreset,
    summary.current?.sms,
    summary.previous?.sms,
    timeseriesBucket,
  ]);

  const summaryCards = [
    { title: 'Current range', data: summary.current },
    { title: 'Previous range', data: summary.previous },
  ];

  const statusCurrent = normalizeStatusItems(breakdowns.current?.status);
  const statusPrevious = normalizeStatusItems(breakdowns.previous?.status);
  const failedReasonCurrent = normalizeFailedReasonItems(breakdowns.current?.failed_reasons);
  const failedReasonPrevious = normalizeFailedReasonItems(breakdowns.previous?.failed_reasons);
  const smsTypeCurrent = normalizeTypeItems(breakdowns.current?.message_types?.sms);
  const smsTypePrevious = normalizeTypeItems(breakdowns.previous?.message_types?.sms);
  const orgCurrent = normalizeOrgItems(breakdowns.current?.organizations);
  const orgPrevious = normalizeOrgItems(breakdowns.previous?.organizations);
  const chartRangeLabels = React.useMemo(() => {
    const currentStart = overview?.filters?.start_at ? new Date(overview.filters.start_at) : new Date(effectiveStartAtMs);
    const currentEnd = overview?.filters?.end_at ? new Date(overview.filters.end_at) : new Date(effectiveEndAtMs);
    const previousStart = overview?.filters?.previous_start_at ? new Date(overview.filters.previous_start_at) : new Date(effectiveStartAtMs - rangeDurationMs);
    const previousEnd = overview?.filters?.previous_end_at ? new Date(overview.filters.previous_end_at) : new Date(effectiveStartAtMs);
    return {
      current: formatShortRangeLabel(currentStart, currentEnd),
      previous: formatShortRangeLabel(previousStart, previousEnd),
      title: `${formatShortRangeLabel(currentStart, currentEnd)} vs ${formatShortRangeLabel(previousStart, previousEnd)}`,
    };
  }, [
    effectiveEndAtMs,
    effectiveStartAtMs,
    overview?.filters?.end_at,
    overview?.filters?.previous_end_at,
    overview?.filters?.previous_start_at,
    overview?.filters?.start_at,
    rangeDurationMs,
  ]);
  const messageTabs = Object.values(MESSAGE_PAGE_TABS);

  const openMessageDetails = React.useCallback(filters => {
    setDrilldownRequest({
      ...filters,
      key: JSON.stringify({ ...filters, ts: Date.now() }),
    });
    setActiveTab(MESSAGE_PAGE_TABS.Details);
  }, []);

  const openTimeseriesDrilldown = React.useCallback((payload, periodKey) => {
    if (!payload) {
      return;
    }

    openMessageDetails({
      start_at: payload[`${periodKey}_start_at`],
      end_at: payload[`${periodKey}_end_at`],
    });
  }, [openMessageDetails]);

  const openCurrentBreakdownDrilldown = React.useCallback(extraFilters => {
    openMessageDetails({
      start_at: new Date(effectiveStartAtMs).toISOString(),
      end_at: new Date(effectiveEndAtMs).toISOString(),
      ...extraFilters,
    });
  }, [effectiveEndAtMs, effectiveStartAtMs, openMessageDetails]);

  const openPreviousBreakdownDrilldown = React.useCallback(extraFilters => {
    openMessageDetails({
      start_at: overview?.filters?.previous_start_at,
      end_at: overview?.filters?.previous_end_at,
      ...extraFilters,
    });
  }, [openMessageDetails, overview?.filters?.previous_end_at, overview?.filters?.previous_start_at]);

  const goBackToOverview = React.useCallback(() => {
    setActiveTab(MESSAGE_PAGE_TABS.Overview);
  }, []);

  return (
    <div className="health-messages">
      <div className="tab-view">
        <div className="tabs">
          <Carousel type="navMenu" activeTabName={activeTab}>
            {messageTabs.map(tabName => (
              <a
                key={tabName}
                className={activeTab === tabName ? 'active' : ''}
                onClick={() => setActiveTab(tabName)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setActiveTab(tabName);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {tabName}
              </a>
            ))}
          </Carousel>
        </div>

        <div className={`tab-content ${activeTab === MESSAGE_PAGE_TABS.Overview ? 'active' : ''}`}>
          <PageHeader
            growRight
            left={Strings.superuserHealthMessagesTitle || 'Messages overview'}
            right={(
              <div className="health-messages__header-controls">
                <div className="health-messages__range-nav health-messages__range-nav--centered">
                  <button type="button" className="health-messages__range-nav-button" onClick={moveRangeLeft}>
                    ←
                  </button>
                  <div className="health-messages__range-label">{rangeLabel}</div>
                  <button
                    type="button"
                    className="health-messages__range-nav-button"
                    onClick={moveRangeRight}
                    disabled={isAtLatestRange}
                  >
                    →
                  </button>
                </div>
                <div className="health-messages__header-actions">
                  <select
                    className="health-messages__range-select"
                    value={rangePreset}
                    onChange={onPresetChange}
                  >
                    {MESSAGE_RANGE_PRESETS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <OrgSelect />
                  <button type="button" className="health-messages__refresh" onClick={onRefresh}>
                    {Strings.refresh || 'Refresh'}
                  </button>
                </div>
              </div>
            )}
          />
          <div className="health-messages__subtitle health-messages__subtitle--page">
            {Strings.superuserHealthMessagesSubtitle ||
              'SMS volume, current status mix, and organization-level trends.'}
          </div>

          <LoadingRenderer loading={isLoading}>
            <>
              {error ? <div className="health-messages-error">{error}</div> : null}

              {!error && overview ? (
                <>
                  <div className="health-messages-grid health-messages-grid--summary">
                    {summaryCards.map(card => (
                      <SummaryCard
                        key={card.title}
                        title={card.title}
                        value={formatCompactNumber(card.data?.total)}
                        subtitle={renderSummarySubtitle(card.data)}
                        accent={(card.data?.failed_rate || 0) > 0.05 ? 'danger' : 'muted'}
                      />
                    ))}
                  </div>

                  <div className="health-messages-grid health-messages-grid--single-chart">
                    <MessageVolumeChart
                      title={`SMS volume: ${chartRangeLabels.title}`}
                      data={timeseries}
                      currentLabel={chartRangeLabels.current}
                      previousLabel={chartRangeLabels.previous}
                      color="#205375"
                      previousColor="#8fa3b8"
                      compactTicks={rangePreset === 'last_1h'}
                      onCurrentClick={payload => openTimeseriesDrilldown(payload, 'current')}
                      onPreviousClick={payload => openTimeseriesDrilldown(payload, 'previous')}
                    />
                  </div>

                  <BreakdownSection title="Status breakdown">
                    <div className="health-messages-grid health-messages-grid--breakdowns">
                      <StatusBreakdownChart
                        title={<PeriodPanelTitle label="Selected period" range={chartRangeLabels.current} />}
                        data={statusCurrent}
                        onBarClick={payload => openCurrentBreakdownDrilldown({ statuses: payload?.status ? [payload.status] : [] })}
                      />
                      <StatusBreakdownChart
                        title={<PeriodPanelTitle label="Previous period" range={chartRangeLabels.previous} />}
                        data={statusPrevious}
                        colorPalette={fadePalette(STATUS_COLORS)}
                        onBarClick={payload => openPreviousBreakdownDrilldown({ statuses: payload?.status ? [payload.status] : [] })}
                      />
                    </div>
                  </BreakdownSection>

                  <BreakdownSection title="Failed SMS reasons">
                    <div className="health-messages-grid health-messages-grid--breakdowns">
                      <HorizontalBreakdownChart
                        title={<PeriodPanelTitle label="Selected period" range={chartRangeLabels.current} />}
                        data={failedReasonCurrent}
                        colorPalette={FAILED_REASON_COLORS}
                        yAxisWidth={220}
                        onBarClick={payload => openCurrentBreakdownDrilldown({ fail_reasons: payload?.reason ? [payload.reason] : [] })}
                      />
                      <HorizontalBreakdownChart
                        title={<PeriodPanelTitle label="Previous period" range={chartRangeLabels.previous} />}
                        data={failedReasonPrevious}
                        colorPalette={fadePalette(FAILED_REASON_COLORS)}
                        yAxisWidth={220}
                        onBarClick={payload => openPreviousBreakdownDrilldown({ fail_reasons: payload?.reason ? [payload.reason] : [] })}
                      />
                    </div>
                  </BreakdownSection>

                  <BreakdownSection title="Top SMS types">
                    <div className="health-messages-grid health-messages-grid--breakdowns">
                      <HorizontalBreakdownChart
                        title={<PeriodPanelTitle label="Selected period" range={chartRangeLabels.current} />}
                        data={smsTypeCurrent}
                        colorPalette={SMS_TYPE_COLORS}
                        yAxisWidth={220}
                        onBarClick={payload => openCurrentBreakdownDrilldown({ message_types: payload?.context ? [payload.context] : [] })}
                      />
                      <HorizontalBreakdownChart
                        title={<PeriodPanelTitle label="Previous period" range={chartRangeLabels.previous} />}
                        data={smsTypePrevious}
                        colorPalette={fadePalette(SMS_TYPE_COLORS)}
                        yAxisWidth={220}
                        onBarClick={payload => openPreviousBreakdownDrilldown({ message_types: payload?.context ? [payload.context] : [] })}
                      />
                    </div>
                  </BreakdownSection>

                  <BreakdownSection title="Top organizations">
                    <div className="health-messages-grid health-messages-grid--breakdowns">
                      <HorizontalBreakdownChart
                        title={<PeriodPanelTitle label="Selected period" range={chartRangeLabels.current} />}
                        data={orgCurrent}
                        colorPalette={ORG_COLORS}
                        yAxisWidth={220}
                        onBarClick={payload => openCurrentBreakdownDrilldown({ org_ids: payload?.org_id ? [payload.org_id] : [] })}
                      />
                      <HorizontalBreakdownChart
                        title={<PeriodPanelTitle label="Previous period" range={chartRangeLabels.previous} />}
                        data={orgPrevious}
                        colorPalette={fadePalette(ORG_COLORS)}
                        yAxisWidth={220}
                        onBarClick={payload => openPreviousBreakdownDrilldown({ org_ids: payload?.org_id ? [payload.org_id] : [] })}
                      />
                    </div>
                  </BreakdownSection>
                </>
              ) : null}
            </>
          </LoadingRenderer>
        </div>

        <div className={`tab-content ${activeTab === MESSAGE_PAGE_TABS.Details ? 'active' : ''}`}>
          <MessageDetailsTab drilldownRequest={drilldownRequest} onBack={goBackToOverview} />
        </div>
      </div>
    </div>
  );
}

MessagesOverview.propTypes = {
  getMessagesOverview: PropTypes.func.isRequired,
  orgIds: PropTypes.string,
};

const mapStateToProps = state => ({
  orgIds: state.entities?.orgFilter?.orgIds,
});

const mapDispatchToProps = dispatch => ({
  getMessagesOverview: params => dispatch(actions.getMessagesOverview(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MessagesOverview);
